import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, X, Check, Camera,
  MapPin, Store, CheckCircle2, LayoutDashboard, Pencil, Lock,
} from "lucide-react";

import {
  upsertCommercePartner,
  uploadCommercePartnerLogo,
  listenToUserCommercePartners,
  listenToCommerceData,
} from "../../redux/commercePartnersSlice";
import type { AppDispatch, RootState } from "../../redux/store";
import {
  businessType,
  onboardingTypes,
  getSubcategories,
  hasSubcategories,
  findCategory,
  CategoryItem,
  renderCategoryIcon,
} from "../../CATEGORIES/PartnerCategories";
import appLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   GOOGLE MAPS LOADER
═══════════════════════════════════════════════════════════ */
function loadGoogleMaps(): Promise<void> {
  return new Promise((resolve) => {
    if ((window as any).google?.maps?.places) { resolve(); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) {
      const poll = setInterval(() => {
        if ((window as any).google?.maps?.places) { clearInterval(poll); resolve(); }
      }, 100);
      return;
    }
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
    const script = document.createElement("script");
    script.id  = "gmap-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const poll = setInterval(() => {
        if ((window as any).google?.maps?.places) { clearInterval(poll); resolve(); }
      }, 100);
    };
    document.head.appendChild(script);
  });
}

/* ═══════════════════════════════════════════════════════════
   PER-CATEGORY DASHBOARD ROUTING
═══════════════════════════════════════════════════════════ */
const getDashboardRoute = (
  subcategoryId: number | string | undefined | null,
  brandId?: string | null,
  fallbackTab: string = "products"
): string => {
  const id = brandId ?? "";
  switch (Number(subcategoryId)) {
    // ── Shopping (1) — food & fresh all share FoodDashboard ──
    case 101: // Grocery Stores
    case 102: // Fresh Markets
    case 103: // Bakeries
    case 104: // Butcher Shops
    case 105: // Seafood Markets
    case 106: // Beverage Shops
    case 107: // Organic Stores
    case 108: // Fast Foods
      return `/freshstore/${brandId}`;

    // ── Booking (2) ──
    case 201: return `/DriverDashboard/${brandId}`;      // Ride
    case 203: return `/shortdashboard/${brandId}`;       // Vacation Rentals
    case 204: return `/cleaningdashboard/${brandId}`;    // Cleanings
    case 205: return `/vacantdashboard/${brandId}`;      // Vacant Rentals
    case 206: return `/hoteldashboard/${brandId}`;       // Hotels
    case 207: return `/RestaurantDashboard/${brandId}`;  // Restaurants

    // ── Ticket (202) — parent + every sub-type share one dashboard ──
     case 202:   return `/TicketDashboard/${brandId}`;        // legacy / unspecified
    case 20201: return `/TrainDashboard/${brandId}`;         // Train
    case 20202: return `/BusDashboard/${brandId}`;           // Bus & Coach
    case 20203: return `/FlightDashboard/${brandId}`;        // Air
    case 20204: return `/FerryDashboard/${brandId}`;         // Ferry & Boat
    case 20205: return `/CinemaDashboard/${brandId}`;        // Cinema
    case 20206: return `/ThemeParkDashboard/${brandId}`;     // Theme Park
    case 20207: return `/ConcertDashboard/${brandId}`;       // Concerts
    case 20208: return `/SportsEventDashboard/${brandId}`;   // Sports Events
    case 20209: return `/AttractionDashboard/${brandId}`; 

    
    // ── Classifieds (3) ──
    case 301: return `/propertydashboard/${brandId}`;    // Properties
    case 302: return `/automotivedashboard/${brandId}`;  // Automotive

    // ── Everything else lives inside StoreCentral ──
    default: return `/store/${brandId}/${fallbackTab}`;
  }
};

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — Heartlas red / gold / near-black
═══════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#FBF6EE",
  bgMuted:    "#F7EFDD",
  black:      "#1B0E06",
  blackMid:   "#2A160B",
  blackSoft:  "#3B2013",
  border:     "#EEDFC4",
  borderDark: "#D9C093",
  muted:      "#8A7660",
  mutedSoft:  "#BBA98E",
  gold:       "#F2A93E",
  goldLight:  "#FCEFD4",
  goldBg:     "#FDF6E7",
  red:        "#C8202E",
  redDark:    "#9E1521",
  redSoft:    "#FBE7E9",
};

const GRAD_GOLD = "linear-gradient(135deg,#F2A93E 0%,#FCEFD4 50%,#F2A93E 100%)";
const HEADER_H  = 64;

/* ═══════════════════════════════════════════════════════════
   STEPS — Type → Category → Identity → Visuals →
   Location → Terms → Launch.
═══════════════════════════════════════════════════════════ */
// Base sequence with a stable `key` used to look up render/content
// functions. "subtype" is filtered in/out at render time depending on
// whether the chosen subcategory has children of its own (currently
// only Ticket does) — this is the ONE step that isn't always present.
const BASE_STEPS = [
  { key:"type",      label:"Type",       sub:"How you'll operate" },
  { key:"category",  label:"Category",   sub:"What you sell"      },
  { key:"subtype",   label:"Subtype",    sub:"Narrow it down"     },
  { key:"identity",  label:"Identity",   sub:"Your brand name"    },
  { key:"visuals",   label:"Visuals",    sub:"Logo & branding"    },
  { key:"location",  label:"Location",   sub:"Where you operate"  },
  { key:"terms",     label:"Terms",      sub:"Accept & continue"  },
  { key:"launch",    label:"Launch",     sub:"Review & go live"   },
];

// Converts the selected onboarding type (id 1 = Business, id 2 = Individual)
// into the {isBusiness, isIndividual} boolean flag object that gets saved to
// Firestore — the selected one is true, the other false — instead of saving
// the raw numeric id.
const toOnboardingTypeFlags = (ot: CategoryItem | null) => ({
  isBusiness:   ot?.id === 1,
  isIndividual: ot?.id === 2,
});

/* ═══════════════════════════════════════════════════════════
   ADDRESS STATE
═══════════════════════════════════════════════════════════ */
interface AddressState {
  street:       string;
  city:         string;
  country:      string;
  county:       string;
  neighborhood: string;
  latitude:     number | undefined;
  longitude:    number | undefined;
}
const EMPTY_ADDRESS: AddressState = {
  street:"", city:"", country:"", county:"", neighborhood:"",
  latitude:undefined, longitude:undefined,
};

/* ═══════════════════════════════════════════════════════════
   ANIMATION VARIANTS
═══════════════════════════════════════════════════════════ */
const pv = {
  enter:  { opacity:0, y:36, filter:"blur(6px)" },
  center: { opacity:1, y:0,  filter:"blur(0px)" },
  exit:   { opacity:0, y:-24, filter:"blur(4px)" },
};
const stagger = { center:{ transition:{ staggerChildren:0.07, delayChildren:0.1 } } };
const fu = {
  enter:  { opacity:0, y:18 },
  center: { opacity:1, y:0, transition:{ duration:0.48, ease:[0.22,1,0.36,1] as any } },
};

/* ═══════════════════════════════════════════════════════════
   SHARED PRIMITIVES
═══════════════════════════════════════════════════════════ */
const inputBase: React.CSSProperties = {
  flex:1, border:"none", outline:"none", padding:"13px 16px",
  fontSize:13, fontFamily:"'Montserrat',sans-serif",
  color:T.black, background:"transparent",
};

const LField: React.FC<{ label:string; required?:boolean; children:React.ReactNode; hint?:string }> = ({ label,required,children,hint }) => (
  <div style={{ marginBottom:22 }}>
    <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:T.muted, fontFamily:"'Montserrat',sans-serif", marginBottom:7, textTransform:"uppercase" }}>
      {label}{required && <span style={{ color:T.gold, marginLeft:3 }}>✦</span>}
    </div>
    {children}
    {hint && <div style={{ fontSize:10, color:T.mutedSoft, fontFamily:"'Montserrat',sans-serif", marginTop:5, letterSpacing:"0.04em" }}>{hint}</div>}
  </div>
);

const LInput: React.FC<{
  value:string; onChange:(v:string)=>void; placeholder?:string; type?:string;
  prefix?:React.ReactNode; inputRef?:React.RefObject<HTMLInputElement>; readOnly?:boolean;
}> = ({ value,onChange,placeholder,type="text",prefix,inputRef,readOnly }) => (
  <div
    style={{ display:"flex", border:`1.5px solid ${T.border}`, background:readOnly?T.bgMuted:T.bg, borderRadius:10, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,.04)", opacity:readOnly?0.7:1 }}
    onFocus={e => { if (!readOnly) e.currentTarget.style.borderColor = T.black; }}
    onBlur={e  => { e.currentTarget.style.borderColor = T.border; }}
  >
    {prefix && (
      <div style={{ padding:"0 14px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
        {prefix}
      </div>
    )}
    <input ref={inputRef} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} type={type} readOnly={readOnly}
      style={{ ...inputBase, cursor:readOnly?"not-allowed":"text" }}/>
  </div>
);

const StepLabel: React.FC<{ step:number; total:number }> = ({ step, total }) => (
  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Montserrat',sans-serif", marginBottom:12 }}>
    STEP 0{step} / 0{total}
  </div>
);

const H1s = (isMobile:boolean): React.CSSProperties => ({
  fontFamily:"'Cormorant Garamond',serif", fontSize:isMobile?30:38, fontWeight:300,
  color:T.black, letterSpacing:"0.03em", lineHeight:1.05, margin:"0 0 8px",
});

const SubS: React.CSSProperties = {
  fontSize:12, color:T.muted, fontFamily:"'Montserrat',sans-serif",
  fontWeight:300, lineHeight:1.8, marginBottom:28,
};

const card3D: React.CSSProperties = {
  background:"#fff",
  borderRadius:16,
  border:`1.5px solid ${T.border}`,
  boxShadow:"0 4px 0 #E7D9B8, 0 8px 24px rgba(27,14,6,.08)",
  overflow:"hidden",
};

/* ═══════════════════════════════════════════════════════════
   PARTNER CARD
═══════════════════════════════════════════════════════════ */
const PartnerCard: React.FC<{ partner:any; onEdit:()=>void; onDashboard:()=>void }> = ({ partner,onEdit,onDashboard }) => {
  const initials = (partner.name??"C").split(" ").slice(0,2).map((w:string)=>w[0]?.toUpperCase()??"").join("");
  return (
    <motion.div initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }}
      style={{ ...card3D, display:"flex", alignItems:"center", gap:14, padding:"14px 16px", marginBottom:12 }}>
      <div style={{ width:3, height:52, background:T.red, flexShrink:0, borderRadius:2 }}/>
      <div style={{ width:50, height:50, background:T.bgMuted, overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:12 }}>
        {partner.image
          ? <img src={partner.image} alt={partner.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
          : <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:300, color:T.muted }}>{initials}</span>
        }
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:500, color:T.black, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"0.03em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {partner.name||"Unnamed Brand"}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4, flexWrap:"nowrap" }}>
          {partner.address?.city && (
            <span style={{ fontSize:10, color:T.muted, fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", gap:3, whiteSpace:"nowrap" }}>
              <MapPin size={9}/>{partner.address.city}
            </span>
          )}
          <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.1em", color:"#fff", background:T.red, padding:"2px 8px", borderRadius:999, whiteSpace:"nowrap", flexShrink:0 }}>ACTIVE</span>
        </div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
        <motion.button whileHover={{ scale:1.03 }} whileTap={{ scale:.97 }} onClick={onDashboard}
          style={{ background:GRAD_GOLD, border:"none", color:T.black, padding:"8px 14px", cursor:"pointer", fontSize:10, fontWeight:700, letterSpacing:"0.1em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", gap:5, flexShrink:0, borderRadius:8, whiteSpace:"nowrap" }}>
          <LayoutDashboard size={11}/> DASHBOARD
        </motion.button>
        <motion.button whileHover={{ scale:1.03 }} whileTap={{ scale:.97 }} onClick={onEdit}
          style={{ background:T.bg, border:`1.5px solid ${T.border}`, color:T.black, padding:"8px 14px", cursor:"pointer", fontSize:10, fontWeight:700, letterSpacing:"0.12em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", gap:5, flexShrink:0, borderRadius:8, transition:"all .18s", whiteSpace:"nowrap" }}
          onMouseEnter={e=>{ e.currentTarget.style.background=T.black; e.currentTarget.style.color=T.bg; }}
          onMouseLeave={e=>{ e.currentTarget.style.background=T.bg; e.currentTarget.style.color=T.black; }}>
          <Pencil size={11}/> EDIT
        </motion.button>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const CommerceOnboarding: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { editBrandId } = useParams<{ editBrandId?:string }>();
  const isEdit = !!editBrandId;

  const logoRef         = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  const { user }     = useSelector((s:RootState) => s.auth);
  const { myPartners, myPartnersLoaded, currentPartner } = useSelector((s:RootState) => s.commercePartners);
  const userId = user?.userId ?? "";
  const hasPartner = myPartners.length > 0;
const [mode,      setMode]      = useState<"list"|"form">(isEdit||!hasPartner ? "form" : "list");
  const [step,      setStep]      = useState(1);
  const [saving,    setSaving]    = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [partnerId, setPartnerId] = useState<string|null>(editBrandId ?? null);
  const [error,     setError]     = useState<string|null>(null);
  const [isMobile,  setIsMobile]  = useState(window.innerWidth < 800);
  const [mapsReady, setMapsReady] = useState(!!(window as any).google?.maps?.places);

  const [storeName,   setStoreName]   = useState("");
  const [logoPreview, setLogoPreview] = useState<string|null>(null);
  const [address,     setAddress]     = useState<AddressState>(EMPTY_ADDRESS);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [selectedOnboardingType, setSelectedOnboardingType] = useState<CategoryItem|null>(null);
  const [selectedCategory,       setSelectedCategory]       = useState<CategoryItem|null>(null);
  const [selectedSubcategory,    setSelectedSubcategory]    = useState<CategoryItem|null>(null);
  const [selectedStoreType,      setSelectedStoreType]      = useState<CategoryItem|null>(null);



  const inFormIntentionally = useRef(false);

  // Glass header scroll hide
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastScrollY.current && y > 80) setHeaderVisible(false);
      else setHeaderVisible(true);
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Level 2 — children of the chosen top-level type.
  const availableSubcategories = getSubcategories(selectedCategory?.id);

  // Level 3 — only rendered when the level-2 pick has children of its
  // own. Right now that's Ticket alone, but nothing here is hardcoded
  // to it: add children to any node and the step appears by itself.
  const needsSubtype       = hasSubcategories(selectedSubcategory?.id);
  const availableSubtypes  = getSubcategories(selectedSubcategory?.id);

  // The id actually written to Firestore: always the deepest pick.
  const finalSubcategoryId = selectedStoreType?.id ?? selectedSubcategory?.id ?? null;

  const EXTERNAL_CAT_ROUTES: Record<number, string> = {};

  // Active step list for THIS run through the wizard: storetype only
  // appears when the user picked "Stores" as their subcategory. Ids are
  // renumbered 1..N each time so the rest of the component (which still
  // works off `step: number`) doesn't need to change its comparisons.
  const STEPS = BASE_STEPS
    .filter(s => s.key !== "subtype" || needsSubtype)
    .map((s, i) => ({ ...s, id: i + 1 }));

  const TERMS_STEP  = STEPS.find(s => s.key === "terms")!.id;
  const LAUNCH_STEP = STEPS.find(s => s.key === "launch")!.id;
  const stepKey     = STEPS.find(s => s.id === step)?.key ?? "type";

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => { if (!user) navigate("/AuthSwitch"); }, [user]);

  useEffect(() => {
    if (!userId) return;
    const unsub = dispatch(listenToUserCommercePartners(userId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [userId, dispatch]);

  // When arriving on an edit route, lock into form mode immediately
  useEffect(() => {
    if (!editBrandId) return;
    inFormIntentionally.current = true;
    setMode("form");
    setStep(1);
    setError(null);
    setPartnerId(editBrandId);
    setStoreName("");
    setLogoPreview(null);
    setAddress(EMPTY_ADDRESS);
    setSelectedOnboardingType(null);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedStoreType(null);
    if (autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, [editBrandId]);

  useEffect(() => {
    if (!editBrandId) return;
    const unsub = dispatch(listenToCommerceData(editBrandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [editBrandId, dispatch]);

  // Populate form fields once the partner data streams in
  useEffect(() => {
    if (!isEdit || !currentPartner || currentPartner.brandId !== editBrandId) return;
    setStoreName(currentPartner.name ?? "");
    setLogoPreview(currentPartner.image ?? null);
    setPartnerId(currentPartner.brandId);
    setTermsAccepted((currentPartner as any).termsAccepted ?? false);
    if (currentPartner.address) {
      setAddress({
        street:       currentPartner.address.street ?? "",
        city:         currentPartner.address.city ?? "",
        country:      currentPartner.address.country ?? "",
        county:       (currentPartner.address as any).county ?? "",
        neighborhood: (currentPartner.address as any).neighborhood ?? "",
        latitude:     currentPartner.address.latitude ?? undefined,
        longitude:    currentPartner.address.longitude ?? undefined,
      });
    }
    const otFlags = (currentPartner as any).onboardingType;
    if (otFlags?.isBusiness) {
      setSelectedOnboardingType(onboardingTypes.find(o => o.id === 1) ?? null);
    } else if (otFlags?.isIndividual) {
      setSelectedOnboardingType(onboardingTypes.find(o => o.id === 2) ?? null);
    }
    // The saved subcategoryId is always the DEEPEST node, so walk back up
    // via parentId to rehydrate every level — no per-branch special cases.
    const saved = findCategory((currentPartner as any).subcategoryId);
    if (saved) {
      const parent = findCategory(saved.parentId);
      if (parent && parent.parentId != null) {
        // saved is level 3 (e.g. Theme Park → Ticket → Booking)
        setSelectedStoreType(saved);
        setSelectedSubcategory(parent);
        setSelectedCategory(findCategory(parent.parentId) ?? null);
      } else {
        // saved is level 2 (e.g. Hotels → Booking)
        setSelectedStoreType(null);
        setSelectedSubcategory(saved);
        setSelectedCategory(parent ?? null);
      }
    } else if (currentPartner.categoryId) {
      setSelectedCategory(businessType.find(c => c.id === currentPartner.categoryId) ?? null);
    }
    // stay in form mode — do NOT call setMode here
  }, [currentPartner, editBrandId, isEdit]);

  // Only redirect to list when partners stream in during a NEW registration flow.
  useEffect(() => {
    if (
      isEdit ||
      inFormIntentionally.current ||
      saving ||
      success ||
      uploading ||
      mode !== "form"
    ) return;
    if (myPartners.length > 0) setMode("list");
  }, [myPartners]);

  useEffect(() => {
    if (mapsReady) return;
    loadGoogleMaps().then(() => setMapsReady(true));
  }, []);

  // ═══════════════════════════════════════════════════════════
  // FIX: THE ACTUAL "ADDRESS NOT SAVING" BUG.
  //
  // This was previously a plain function re-created on *every* render
  // (e.g. every keystroke, since typing in the street field updates
  // `address` state → re-render → new function identity). React treats
  // a ref callback's identity as significant: whenever it changes, React
  // calls the OLD ref with `null` (cleanup) and the NEW ref with the DOM
  // node again. That meant the Google Places `Autocomplete` widget was
  // being torn down and rebuilt on every single keystroke — closing the
  // suggestion dropdown out from under the user before `place_changed`
  // could ever fire. Structured fields (city/country/county/neighborhood/
  // lat/lng) that only ever get populated via `place_changed` therefore
  // never got a chance to populate, no matter how much the user typed.
  //
  // Wrapping it in useCallback with a narrow dependency list keeps the
  // function identity — and therefore the bound Autocomplete instance —
  // stable across re-renders caused by typing. It only rebinds when
  // something that actually matters (`mapsReady`, `partnerId`) changes.
  // ═══════════════════════════════════════════════════════════
  const bindAddressAutocomplete = useCallback((input: HTMLInputElement | null) => {
    addressInputRef.current = input;

    if (autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    if (!input || !mapsReady) return;

    const ac = new (window as any).google.maps.places.Autocomplete(input, {
      types:["geocode"], fields:["address_components","geometry","formatted_address"],
    });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place?.address_components) return;
      const comps = place.address_components as Array<{ types:string[]; long_name:string }>;
      const get   = (t:string) => comps.find(c=>c.types.includes(t))?.long_name ?? "";
      const resolved: AddressState = {
        street:       `${get("street_number")} ${get("route")}`.trim(),
        city:         get("locality") || get("administrative_area_level_2"),
        neighborhood: get("sublocality") || get("neighborhood"),
        country:      get("country"),
        county:       get("administrative_area_level_1"),
        latitude:     place.geometry?.location?.lat(),
        longitude:    place.geometry?.location?.lng(),
      };
      setAddress(resolved);
      if (partnerId) {
        dispatch(upsertCommercePartner({ brandId:partnerId, data:{ address:resolved } }) as any);
      }
    });
    autocompleteRef.current = ac;
  }, [mapsReady, partnerId, dispatch]);

  // Rebind autocomplete once Google Maps finishes loading (covers the case
  // where the Location step is already mounted before mapsReady flips true).
  useEffect(() => {
    if (!mapsReady || !addressInputRef.current || autocompleteRef.current) return;
    bindAddressAutocomplete(addressInputRef.current);
  }, [mapsReady, bindAddressAutocomplete]);

  // On success, navigate to dashboard
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => {
      setSuccess(false);
      inFormIntentionally.current = false;
      navigate(getDashboardRoute(finalSubcategoryId, partnerId));
    }, 1800);
    return () => clearTimeout(t);
  }, [success]);

  const enterForm = () => {
    inFormIntentionally.current = true;
    setStoreName(""); setLogoPreview(null); setAddress(EMPTY_ADDRESS);
    setError(null); setStep(1); setPartnerId(null);
    setSelectedOnboardingType(null);
    setSelectedCategory(null); setSelectedSubcategory(null);
    setSelectedStoreType(null);
    setTermsAccepted(false);
    if (autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    setMode("form");
  };

  const handleLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!partnerId) return;
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setLogoPreview(URL.createObjectURL(f));
    setUploading(true);
    dispatch(uploadCommercePartnerLogo({ brandId:partnerId, file:f }) as any)
      .unwrap()
      .catch(() => {
        alert("Logo upload failed.");
        setLogoPreview(null);
      })
      .finally(() => setUploading(false));
  };

  const saveEarly = async (): Promise<boolean> => {
    if (!storeName.trim()) { setError("Brand name is required."); return false; }
    try {
      setSaving(true); setError(null);
      const created = await dispatch(upsertCommercePartner({
        brandId: partnerId ?? undefined,
        data: {
          name: storeName,
          onboardingType: toOnboardingTypeFlags(selectedOnboardingType),
          categoryId:     selectedCategory?.id ?? null,
          subcategoryId:  finalSubcategoryId,
          isDraft:true, isComplete:false, termsAccepted,
        },
      })).unwrap();
      setPartnerId(created.brandId ?? (created as any).partnerId);
      return true;
    } catch {
      setError("Could not save brand. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // FIX: Location step now also saves address explicitly when the user
  // clicks Continue (not just when autocomplete fires or at final submit),
  // so manually-typed street text without picking a Google suggestion
  // is still persisted incrementally, same as every other step.
  const saveAddressStep = async () => {
    if (!partnerId) return;
    if (!address.city && !address.street) return;
    try {
      await dispatch(upsertCommercePartner({
        brandId: partnerId,
        data: { address },
      }) as any).unwrap();
    } catch {
      // Non-fatal — final submit will retry with the full address payload.
    }
  };

  const handleFinalSubmit = async () => {
    if (!partnerId) { setError("Brand not yet saved."); return; }
    if (!termsAccepted) { setError("Please accept the Terms & Conditions to continue."); return; }
    try {
      setSaving(true); setError(null);
      await dispatch(upsertCommercePartner({
        brandId: partnerId,
        data: {
          onboardingType: toOnboardingTypeFlags(selectedOnboardingType),
          
          categoryId:    selectedCategory?.id    ?? null,
          subcategoryId: finalSubcategoryId,
          address:       (address.city || address.street) ? address : undefined,
          termsAccepted: true,
          isDraft:false, isComplete:true,
        },
      })).unwrap();
      setSuccess(true);
    } catch {
      setError("Registration failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectCategory = (cat: CategoryItem) => {
    // No duplicate check here anymore — categoryId alone isn't a
    // reliable signal that this is "the same brand". The real check
    // now happens in goNext() at Step 2, once category + subcategory
    // are both known.
    setSelectedCategory(cat);
    setSelectedSubcategory(null);
  };

  const goNext = async () => {
    setError(null);

    // Step 1 — Onboarding Type
    if (step === 1) {
      if (!selectedOnboardingType) { setError("Please select how you'll operate."); return; }
      setStep(2); return;
    }

    // Category step
    if (stepKey === "category") {
      if (!selectedCategory) { setError("Please select a category."); return; }
      if (EXTERNAL_CAT_ROUTES[selectedCategory.id]) {
        navigate(EXTERNAL_CAT_ROUTES[selectedCategory.id]);
        return;
      }
      if (needsSubtype && !selectedStoreType) {
        // Don't persist yet — a half-finished branch pick (e.g. "Ticket"
        // with no transport type) should never hit Firestore on its own.
        setStep(step + 1); // -> subtype
        return;
      }
      if (partnerId) {
        await dispatch(upsertCommercePartner({
          brandId:partnerId,
          data:{ categoryId:selectedCategory.id, subcategoryId: finalSubcategoryId },
        }) as any).unwrap().catch(()=>{});
      }
      setStep(step + 1); return;
    }

    // Subtype step (only present when the subcategory has children)
    if (stepKey === "subtype") {
      if (!selectedStoreType) { setError(`Please pick a ${selectedSubcategory?.name ?? "subtype"}.`); return; }
      if (partnerId) {
        await dispatch(upsertCommercePartner({
          brandId:partnerId,
          data:{ categoryId:selectedCategory?.id ?? null, subcategoryId:selectedStoreType.id },
        }) as any).unwrap().catch(()=>{});
      }
      setStep(step + 1); return;
    }

    // Identity step (creates the partner document)
    if (stepKey === "identity") {
      if (!storeName.trim()) { setError("Brand name is required."); return; }
      const saved = await saveEarly();
      if (!saved) return;
      setStep(step + 1); return;
    }

    // Visuals (logo) step
    if (stepKey === "visuals") { setStep(step + 1); return; }

    // Location step
    if (stepKey === "location") { await saveAddressStep(); setStep(step + 1); return; }

    // Terms & Conditions step
    if (step === TERMS_STEP) {
      if (!termsAccepted) { setError("Please accept the Terms & Conditions to continue."); return; }
      setStep(LAUNCH_STEP); return;
    }

    // Review & Launch step
    if (step === LAUNCH_STEP) { await handleFinalSubmit(); }
  };

  const goPrev = () => {
    setError(null);
    if (step > 1) setStep(s => s-1);
    else {
      inFormIntentionally.current = false;
      setMode("list");
    }
  };

  /* ═══════════════════════════════════════════════════════
     LIST MODE
  ═══════════════════════════════════════════════════════ */
  const renderList = () => (
    <motion.div key="list" variants={pv} initial="enter" animate="center" exit="exit"
      transition={{ duration:0.4, ease:[0.22,1,0.36,1] }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:9, letterSpacing:"0.25em", color:T.muted, fontFamily:"'Montserrat',sans-serif", fontWeight:600, marginBottom:10 }}>
          COMMERCE SELLER
        </div>
        <h1 style={H1s(isMobile)}>My Brands</h1>
        <p style={{ fontSize:12, color:T.muted, fontFamily:"'Montserrat',sans-serif", fontWeight:300 }}>
          {myPartners.length} registered brand{myPartners.length !== 1?"s":""}
        </p>
      </div>

      {myPartners.map((p:any) => (
        <PartnerCard key={p.brandId} partner={p}
          onEdit={() => navigate(`/CommerceOnboarding/${p.brandId}`)}
          onDashboard={() => navigate(getDashboardRoute(p.subcategoryId, p.brandId))}
        />
      ))}

      <div style={{ height:1, background:T.border, margin:"20px 0 18px" }}/>

      <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:.98 }} onClick={enterForm}
        style={{ width:"100%", background:T.black, color:T.bg, border:"none", padding:"15px 0", cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.2em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:10, borderRadius:12, boxShadow:"0 4px 0 #3B2013, 0 8px 20px rgba(27,14,6,.24)" }}>
        + ADD NEW BRAND
      </motion.button>

      <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:.98 }}
        onClick={() => navigate(`/SellerDashboard/${userId}`)}
        style={{ width:"100%", background:T.bg, color:T.black, border:`1.5px solid ${T.border}`, padding:"14px 0", cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.2em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, borderRadius:12, boxShadow:"0 4px 0 #EEDFC4, 0 8px 16px rgba(27,14,6,.06)", transition:"all .18s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor=T.black}
        onMouseLeave={e => e.currentTarget.style.borderColor=T.border}>
        <LayoutDashboard size={13}/> GO TO DASHBOARD
      </motion.button>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 1 — ONBOARDING TYPE
  ═══════════════════════════════════════════════════════ */
  const renderStepType = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={1} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>How Will You Operate?</h1>
        <p style={SubS}>Choose the onboarding type that best fits your business.</p>
      </motion.div>

      <motion.div variants={fu}>
        <LField label="Onboarding Type" required>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:10 }}>
            {onboardingTypes.map((ot: CategoryItem) => {
              const isSel = selectedOnboardingType?.id === ot.id;
              return (
                <motion.div key={ot.id}
                  whileHover={{ y:-3, boxShadow:`0 8px 0 ${ot.color ?? T.gold}44, 0 16px 28px rgba(27,14,6,.12)` }}
                  whileTap={{ scale:.96 }}
                  onClick={() => setSelectedOnboardingType(ot)}
                  style={{
                    border: isSel ? `2px solid ${ot.color??T.gold}` : `1.5px solid ${T.border}`,
                    borderRadius:14, padding:"14px 8px", cursor:"pointer",
                    background: isSel ? `${ot.color??T.gold}12` : T.bg,
                    boxShadow: isSel
                      ? `0 4px 0 ${ot.color??T.gold}66, 0 8px 20px rgba(27,14,6,.10)`
                      : "0 4px 0 #EEDFC4, 0 8px 16px rgba(27,14,6,.05)",
                    display:"flex", flexDirection:"column", alignItems:"center", gap:8,
                    transition:"border-color .15s, background .15s",
                  }}>
                  <div style={{ width:40, height:40, background:`${ot.color??T.gold}18`, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:12 }}>
                    {renderCategoryIcon(ot.icon, ot.color??T.gold, 20)}
                  </div>
                  <span style={{ fontSize:9, fontWeight:isSel?700:500, color:isSel?(ot.color??T.gold):T.black, fontFamily:"'Montserrat',sans-serif", textAlign:"center", letterSpacing:"0.04em", lineHeight:1.3 }}>
                    {ot.name}
                  </span>
                  {isSel && <Check size={10} color={ot.color??T.gold}/>}
                </motion.div>
              );
            })}
          </div>
        </LField>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 2 — CATEGORY
  ═══════════════════════════════════════════════════════ */
  const renderStepCategory = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={2} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>Business Category</h1>
        <p style={SubS}>What type of business are you registering?</p>
      </motion.div>

      <motion.div variants={fu}>
        <LField label="Category" required>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:10, marginBottom:16 }}>
            {businessType.map(cat => {
              const isSel = selectedCategory?.id === cat.id;
              const isExternal = !!EXTERNAL_CAT_ROUTES[cat.id];
              const hasOwnBrand = myPartners.some((p:any) => Number(p.categoryId) === Number(cat.id) && p.brandId !== partnerId);
              return (
                <motion.div key={cat.id}
                  whileHover={{ y:-3, boxShadow:`0 8px 0 ${cat.color ?? T.gold}44, 0 16px 28px rgba(27,14,6,.12)` }}
                  whileTap={{ scale:.96 }}
                  onClick={() => handleSelectCategory(cat)}
                  style={{
                    border: isSel ? `2px solid ${cat.color??T.gold}` : `1.5px solid ${T.border}`,
                    borderRadius:14, padding:"14px 8px", cursor:"pointer",
                    background: isSel ? `${cat.color??T.gold}12` : T.bg,
                    boxShadow: isSel
                      ? `0 4px 0 ${cat.color??T.gold}66, 0 8px 20px rgba(27,14,6,.10)`
                      : "0 4px 0 #EEDFC4, 0 8px 16px rgba(27,14,6,.05)",
                    display:"flex", flexDirection:"column", alignItems:"center", gap:8,
                    transition:"border-color .15s, background .15s",
                    position:"relative",
                  }}>
                  {(isExternal || hasOwnBrand) && (
                    <div style={{ position:"absolute", top:5, right:7, fontSize:7, fontWeight:700, letterSpacing:"0.1em", color:T.muted, fontFamily:"'Montserrat',sans-serif" }}>
                      {isExternal ? "↗" : "●"}
                    </div>
                  )}
                  <div style={{ width:40, height:40, background:`${cat.color??T.gold}18`, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:12 }}>
                    {renderCategoryIcon(cat.icon, cat.color??T.gold, 20)}
                  </div>
                  <span style={{ fontSize:9, fontWeight:isSel?700:500, color:isSel?(cat.color??T.gold):T.black, fontFamily:"'Montserrat',sans-serif", textAlign:"center", letterSpacing:"0.04em", lineHeight:1.3 }}>
                    {cat.name}
                  </span>
                  {isSel && <Check size={10} color={cat.color??T.gold}/>}
                </motion.div>
              );
            })}
          </div>
        </LField>

        {selectedCategory && availableSubcategories.length > 0 && (
          <motion.div initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }}>
            <LField label="Subcategory" hint="Optional — helps customers find you">
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {availableSubcategories.map(sub => {
                  const isSel = selectedSubcategory?.id === sub.id;
                  return (
                    <motion.button key={sub.id}
                      whileHover={{ y:-2 }}
                      whileTap={{ scale:.95 }}
                      onClick={() => {
                        setSelectedSubcategory(isSel?null:sub);
                        setSelectedStoreType(null); // reset if switching subcategory
                      }}
                      style={{
                        padding:"8px 14px", borderRadius:999,
                        border: isSel ? `2px solid ${sub.color??T.black}` : `1.5px solid ${T.border}`,
                        background: isSel ? `${sub.color??T.black}12` : T.bg,
                        color: isSel ? (sub.color??T.black) : T.black,
                        fontSize:11, fontWeight:isSel?700:400,
                        cursor:"pointer", fontFamily:"'Montserrat',sans-serif",
                        letterSpacing:"0.04em",
                        boxShadow: isSel ? `0 3px 0 ${sub.color??T.black}44` : "0 2px 0 #EEDFC4",
                        transition:"all .15s",
                        display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
                      }}>
                      <div style={{ width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {renderCategoryIcon(sub.icon, sub.color??T.gold, 12)}
                      </div>
                      {sub.name}
                    </motion.button>
                  );
                })}
              </div>
            </LField>
          </motion.div>
        )}

        {selectedCategory && EXTERNAL_CAT_ROUTES[selectedCategory.id] && (
          <motion.div initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }}
            style={{ padding:"12px 16px", background:"#FDF6E7", border:`1px solid ${T.goldLight}`, borderRadius:10, marginTop:8 }}>
            <div style={{ fontSize:11, color:"#7A5C00", fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em", lineHeight:1.7 }}>
              ℹ️ <strong>{selectedCategory.name}</strong> has a dedicated registration portal. Clicking Continue will take you there.
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );


  /* ═══════════════════════════════════════════════════════
     STEP — STORE TYPE (conditional, only when Stores picked)
  ═══════════════════════════════════════════════════════ */
  const renderStepStoreType = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={STEPS.find(s=>s.key==="subtype")?.id ?? 3} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>What Kind of {selectedSubcategory?.name ?? "Business"}?</h1>
        <p style={SubS}>Pick the option that best matches what you offer.</p>
      </motion.div>
      <motion.div variants={fu}>
        <LField label={selectedSubcategory?.name ?? "Subtype"} required>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {availableSubtypes.map(st => {
              const isSel = selectedStoreType?.id === st.id;
              return (
                <motion.button key={st.id}
                  whileHover={{ y:-2 }}
                  whileTap={{ scale:.95 }}
                  onClick={() => setSelectedStoreType(isSel?null:st)}
                  style={{
                    padding:"8px 14px", borderRadius:999,
                    border: isSel ? `2px solid ${st.color??T.black}` : `1.5px solid ${T.border}`,
                    background: isSel ? `${st.color??T.black}12` : T.bg,
                    color: isSel ? (st.color??T.black) : T.black,
                    fontSize:11, fontWeight:isSel?700:400,
                    cursor:"pointer", fontFamily:"'Montserrat',sans-serif",
                    letterSpacing:"0.04em",
                    boxShadow: isSel ? `0 3px 0 ${st.color??T.black}44` : "0 2px 0 #EEDFC4",
                    transition:"all .15s",
                    display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
                  }}>
                  <div style={{ width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {renderCategoryIcon(st.icon, st.color??T.gold, 12)}
                  </div>
                  {st.name}
                </motion.button>
              );
            })}
          </div>
        </LField>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 3 — IDENTITY
  ═══════════════════════════════════════════════════════ */
  const renderStepIdentity = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={STEPS.find(s=>s.key==="identity")!.id} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>{isEdit?"Edit Brand":"Brand Identity"}</h1>
        <p style={SubS}>{isEdit?"Update your brand name.":"Choose a name that represents your brand."}</p>
      </motion.div>
      <motion.div variants={fu}>
        <LField label="Brand Name" required>
          <LInput value={storeName} onChange={setStoreName} placeholder="e.g. My Bakery, Tech Store…"/>
        </LField>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 4 — LOGO
  ═══════════════════════════════════════════════════════ */
  const renderStepLogo = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={STEPS.find(s=>s.key==="visuals")!.id} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>Brand Logo</h1>
        <p style={SubS}>Upload your logo — or skip and add it later.</p>
      </motion.div>
      <motion.div variants={fu} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14, marginBottom:24 }}>
        <label htmlFor="logo-upload-input"
          style={{
            width:130, height:130,
            background:logoPreview?"transparent":T.bgMuted,
            border:`2px dashed ${T.borderDark}`,
            cursor:partnerId?(uploading?"wait":"pointer"):"not-allowed",
            opacity:partnerId?1:0.5,
            overflow:"hidden",
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8,
            position:"relative", borderRadius:20,
            boxShadow:"0 4px 0 #EEDFC4, 0 8px 24px rgba(27,14,6,.06)",
          }}>
          {logoPreview
            ? <img src={logoPreview} alt="logo" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
            : <><Camera size={26} color={T.muted}/><span style={{ fontSize:9, color:T.muted, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.1em" }}>UPLOAD LOGO</span></>
          }
          {uploading && (
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(255,255,255,0.75)", borderRadius:20 }}>
              <div style={{ width:24, height:24, borderRadius:"50%", border:`3px solid ${T.black}`, borderTopColor:"transparent", animation:"glamSpin 0.8s linear infinite" }}/>
            </div>
          )}
        </label>
        <input
          id="logo-upload-input"
          ref={logoRef}
          type="file"
          hidden
          accept="image/*"
          disabled={!partnerId || uploading}
          onChange={handleLogo}
        />

        {!partnerId && (
          <div style={{ fontSize:10, color:T.mutedSoft, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em", textAlign:"center" }}>
            Save brand name first to unlock logo upload
          </div>
        )}

        {partnerId && (
          <motion.div initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:T.goldBg, border:`1px solid ${T.goldLight}`, borderRadius:10 }}>
            <Check size={12} color={T.gold}/>
            <span style={{ fontSize:10, color:"#7A5C00", fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em" }}>
              {logoPreview ? "Logo uploaded ✓" : "You can skip and add a logo later"}
            </span>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 5 — LOCATION
  ═══════════════════════════════════════════════════════ */
  const renderStepLocation = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={STEPS.find(s=>s.key==="location")!.id} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>Location</h1>
        <p style={SubS}>Where is your store based? Helps match you with nearby customers.</p>
      </motion.div>

      <motion.div variants={fu}>
        {!mapsReady && (
          <div style={{ fontSize:11, color:T.gold, fontFamily:"'Montserrat',sans-serif", padding:"10px 14px", background:T.goldBg, border:`1px solid ${T.goldLight}`, marginBottom:14, letterSpacing:"0.06em", borderRadius:10 }}>
            Loading address search…
          </div>
        )}

        <LField label="Business Address" hint="Start typing and select from the dropdown">
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, background:T.bg, borderRadius:10, overflow:"hidden", boxShadow:"0 2px 8px rgba(27,14,6,.04)" }}>
            <div style={{ padding:"0 14px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
              <MapPin size={14}/>
            </div>
            <input
              ref={bindAddressAutocomplete}
              value={address.street}
              onChange={e => setAddress(a => ({ ...a, street:e.target.value }))}
              placeholder={mapsReady?"Enter your address…":"Loading maps…"}
              type="text"
              autoComplete="off"
              style={{ ...inputBase, pointerEvents:mapsReady?"auto":"none", opacity:mapsReady?1:0.5 }}
            />
          </div>
        </LField>

        {address.city && (
          <motion.div initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }}
            style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:-10 }}>
            {[
              { label:"City",         val:address.city         },
              { label:"Country",      val:address.country      },
              { label:"Region",       val:address.county       },
              { label:"Neighborhood", val:address.neighborhood },
            ].filter(f=>f.val).map(f => (
              <div key={f.label}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:T.muted, fontFamily:"'Montserrat',sans-serif", marginBottom:5 }}>{f.label.toUpperCase()}</div>
                <div style={{ fontSize:13, color:T.black, fontFamily:"'Montserrat',sans-serif", padding:"10px 14px", background:T.bgMuted, border:`1px solid ${T.border}`, borderRadius:8 }}>{f.val}</div>
              </div>
            ))}
            {address.latitude !== undefined && (
              <div style={{ gridColumn:"1/-1" }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:T.muted, fontFamily:"'Montserrat',sans-serif", marginBottom:5 }}>COORDINATES</div>
                <div style={{ fontSize:11, color:T.muted, fontFamily:"'Montserrat',sans-serif", padding:"8px 14px", background:T.bgMuted, border:`1px solid ${T.border}`, borderRadius:8, letterSpacing:"0.04em" }}>
                  {address.latitude?.toFixed(6)}, {address.longitude?.toFixed(6)}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 6 — TERMS & CONDITIONS
  ═══════════════════════════════════════════════════════ */
  const renderStepTerms = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={TERMS_STEP} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>Terms &amp; Conditions</h1>
        <p style={SubS}>
          {termsAccepted
            ? "You've accepted our seller terms. This step is now locked."
            : "Please review and accept our seller terms before continuing."}
        </p>
      </motion.div>

      <motion.div variants={fu}
        style={{
          ...card3D,
          padding:"22px 20px",
          display:"flex",
          alignItems:"center",
          gap:16,
          marginBottom:18,
          borderColor: T.border,
          background: termsAccepted ? T.bgMuted : T.bg,
          opacity: termsAccepted ? 0.65 : 1,
          filter: termsAccepted ? "grayscale(0.4)" : "none",
          transition:"border-color .2s, background .2s, opacity .2s, filter .2s",
        }}>
        <button
          type="button"
          onClick={() => { if (!termsAccepted) setTermsAccepted(true); }}
          disabled={termsAccepted}
          aria-pressed={termsAccepted}
          aria-label="Accept Terms and Conditions"
          style={{
            width:52, height:30, borderRadius:999, border:"none",
            cursor: termsAccepted ? "not-allowed" : "pointer",
            background: termsAccepted ? T.mutedSoft : T.borderDark,
            position:"relative", flexShrink:0, transition:"background .2s",
          }}>
          <motion.div
            animate={{ left: termsAccepted ? 24 : 3 }}
            transition={{ type:"spring", stiffness:500, damping:32 }}
            style={{ position:"absolute", top:3, width:24, height:24, borderRadius:"50%", background:"#fff", boxShadow:"0 2px 6px rgba(27,14,6,.28)" }}/>
        </button>

        <div style={{ flex:1 }}>
          <div style={{ fontSize:12.5, color:T.black, fontFamily:"'Montserrat',sans-serif", fontWeight:500, lineHeight:1.6 }}>
            {termsAccepted ? (
              "Terms & Conditions accepted"
            ) : (
              <>
                I have read and agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                  style={{ color:T.red, fontWeight:700, textDecoration:"underline", textUnderlineOffset:3 }}>
                  Terms &amp; Conditions
                </a>
                {" "}for Commerce Sellers.
              </>
            )}
          </div>
          <div style={{ fontSize:10, color:T.muted, fontFamily:"'Montserrat',sans-serif", marginTop:6, letterSpacing:"0.02em" }}>
            {termsAccepted ? "This can only be changed by contacting support." : "Required to register your brand and continue."}
          </div>
        </div>

        {termsAccepted && (
          <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ type:"spring", stiffness:400, damping:18 }}
            style={{ width:26, height:26, borderRadius:"50%", background:T.mutedSoft, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <Lock size={12} color="#fff"/>
          </motion.div>
        )}
      </motion.div>

      <motion.div variants={fu} style={{ fontSize:10.5, color:T.mutedSoft, fontFamily:"'Montserrat',sans-serif", lineHeight:1.8, letterSpacing:"0.02em" }}>
        Heartlas reviews every new store before it goes live.
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     STEP 7 — REVIEW & LAUNCH
  ═══════════════════════════════════════════════════════ */
  const renderStepReview = () => (
    <motion.div variants={stagger} initial="enter" animate="center">
      <motion.div variants={fu}><StepLabel step={LAUNCH_STEP} total={STEPS.length}/></motion.div>
      <motion.div variants={fu}>
        <h1 style={H1s(isMobile)}>{isEdit?"Review & Save":"Review & Launch"}</h1>
        <p style={SubS}>{isEdit?"Confirm your changes.":"Confirm your details and go live as a Commerce seller."}</p>
      </motion.div>

      <motion.div variants={fu} style={{ ...card3D, marginBottom:24 }}>
        <div style={{ height:80, background:`linear-gradient(135deg,${T.bgMuted},${T.border})`, position:"relative" }}>
          <div style={{ position:"absolute", bottom:-24, left:20, width:48, height:48, background:T.bg, border:`2px solid ${T.bg}`, overflow:"hidden", borderRadius:12, boxShadow:"0 4px 12px rgba(27,14,6,.14)" }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
              : <div style={{ width:"100%", height:"100%", background:T.bgMuted, display:"flex", alignItems:"center", justifyContent:"center" }}><Store size={18} color={T.muted}/></div>
            }
          </div>
        </div>
        <div style={{ padding:"32px 20px 20px" }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:400, color:T.black, marginBottom:12 }}>{storeName}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
            {[
              ["Type",     selectedOnboardingType?.name ||"—"],
              ["City",     address.city          ||"—"],
              ["Country",  address.country        ||"—"],
              ["Category", selectedCategory?.name ||"—"],
              ["Subtype",  selectedStoreType?.name || selectedSubcategory?.name || "—"],
            ].map(([k,v]) => (
              <div key={k} style={{ padding:"10px 0", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Montserrat',sans-serif", marginBottom:3 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize:12, color:T.black, fontFamily:"'Montserrat',sans-serif", fontWeight:500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div variants={fu} style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:termsAccepted?T.goldBg:T.redSoft, border:`1px solid ${termsAccepted?T.goldLight:"#F3B9BE"}`, marginBottom:12, borderRadius:10 }}>
        <Check size={12} color={termsAccepted?T.gold:T.red}/>
        <span style={{ fontSize:10.5, color:termsAccepted?"#7A5C00":T.redDark, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em" }}>
          {termsAccepted ? "Terms & Conditions accepted" : "Terms & Conditions not yet accepted"}
        </span>
      </motion.div>

      <motion.div variants={fu} style={{ padding:"12px 16px", background:T.goldBg, border:`1px solid ${T.goldLight}`, marginBottom:8, borderRadius:10 }}>
        <div style={{ fontSize:10, color:"#7A5C00", fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em", lineHeight:1.7 }}>
          {isEdit
            ? "Your changes will be saved and applied immediately."
            : "By registering, you agree to Commerce Seller Terms. Your store will be reviewed before going live."
          }
        </div>
      </motion.div>
    </motion.div>
  );

  /* ═══════════════════════════════════════════════════════
     SUCCESS OVERLAY
  ═══════════════════════════════════════════════════════ */
  const SuccessOverlay = () => (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
      style={{ position:"absolute", inset:0, background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:20, gap:16, padding:32, borderRadius:20 }}>
      <motion.div initial={{ scale:0,rotate:-90 }} animate={{ scale:1,rotate:0 }}
        transition={{ type:"spring", stiffness:160, damping:14 }}
        style={{ width:72, height:72, borderRadius:"50%", background:GRAD_GOLD, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 8px 0 #F2A93E44, 0 16px 32px rgba(242,169,62,.32)" }}>
        <motion.svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <motion.path d="M5 13l4 4L19 7" initial={{ pathLength:0 }} animate={{ pathLength:1 }} transition={{ delay:0.4, duration:0.6, ease:"easeOut" }}/>
        </motion.svg>
      </motion.div>
      <motion.h2 initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.3 }}
        style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:300, color:T.black, margin:0, letterSpacing:"0.04em", fontStyle:"italic" }}>
        {isEdit?"Changes Saved":"Store Registered"}
      </motion.h2>
      <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.5 }}
        style={{ fontSize:12, color:T.muted, fontFamily:"'Montserrat',sans-serif", textAlign:"center", margin:0, lineHeight:1.7 }}>
        {isEdit?"Redirecting to dashboard…":"Welcome to Commerce Seller. Redirecting…"}
      </motion.p>
      <motion.div style={{ width:160, height:2, background:T.bgMuted, overflow:"hidden", marginTop:16, borderRadius:999 }}>
        <motion.div initial={{ width:"0%" }} animate={{ width:"100%" }}
          transition={{ delay:0.4, duration:1.4, ease:"linear" }}
          style={{ height:"100%", background:GRAD_GOLD }}/>
      </motion.div>
    </motion.div>
  );

  const stepContent: Record<string,()=>React.ReactNode> = {
    type:renderStepType, category:renderStepCategory, subtype:renderStepStoreType,
    identity:renderStepIdentity, visuals:renderStepLogo,
    location:renderStepLocation, terms:renderStepTerms, launch:renderStepReview,
  };

  /* ═══════════════════════════════════════════════════════
     ROOT RENDER
  ═══════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:#170905; }
        input::placeholder, textarea::placeholder { color:#BBA98E; }
        ::-webkit-scrollbar { display:none; }
        @keyframes glamSpin { to { transform:rotate(360deg); } }
        .pac-container { font-family:'Montserrat',sans-serif !important; font-size:12px !important; border:1.5px solid #EEDFC4 !important; border-top:none !important; box-shadow:0 8px 24px rgba(27,14,6,.10) !important; border-radius:0 0 12px 12px !important; z-index:99999 !important; }
        .pac-item { padding:10px 14px !important; border-top:1px solid #F7EFDD !important; color:#2A160B !important; cursor:pointer !important; line-height:1.5 !important; }
        .pac-item:hover, .pac-item-selected { background:#FBF6EE !important; }
        .pac-item-query { font-size:12px !important; color:#1B0E06 !important; font-weight:500 !important; }
        .pac-icon { display:none !important; }
        .pac-matched { color:#F2A93E !important; font-weight:700 !important; }
      `}</style>

      {/* Glass header */}
      <header style={{
        position:"fixed", top:0, left:0, right:0, height:HEADER_H,
        background:"rgba(23,9,5,0.6)",
        backdropFilter:"blur(18px)",
        WebkitBackdropFilter:"blur(18px)",
        borderBottom:"1px solid rgba(255,255,255,0.08)",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 20px", zIndex:300,
        transform: headerVisible ? "translateY(0)" : "translateY(-100%)",
        transition:"transform 0.28s ease",
      }}>
        <button onClick={() => navigate(-1)}
          style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,.6)", fontFamily:"'Montserrat',sans-serif", fontSize:11, letterSpacing:"0.12em", display:"flex", alignItems:"center", gap:6 }}>
          <ArrowLeft size={14}/> BACK
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <img src={appLogo} alt="Heartlas" style={{ width:22, height:22, borderRadius:6, objectFit:"cover" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:300, letterSpacing:"0.3em", color:"#FFFFFF" }}>
            COMMERCE
          </div>
        </div>
        <div style={{ width:80 }}/>
      </header>

      <div style={{
        position:"relative",
        minHeight:"100vh",
        background:"linear-gradient(160deg,#170905 0%,#2A160B 50%,#170905 100%)",
        display:"flex", justifyContent:"center", alignItems:"flex-start",
        padding: isMobile ? `${HEADER_H+16}px 16px 40px` : `${HEADER_H+40}px 24px 60px`,
        overflow:"hidden",
      }}>

        <img
          src={appLogo}
          alt=""
          aria-hidden="true"
          style={{
            position:"fixed", top:"50%", left:"50%",
            width:560, height:560, maxWidth:"90vw", maxHeight:"90vw",
            transform:"translate(-50%,-50%)",
            opacity:0.05, pointerEvents:"none", zIndex:0, objectFit:"contain",
          }}
        />

        <div style={{ width:"100%", maxWidth:560, position:"relative", zIndex:1 }}>

          <div style={{ ...card3D, padding:isMobile?"28px 20px 32px":"40px 36px 44px", position:"relative", overflow:"hidden", minHeight:400 }}>

            <img
              src={appLogo}
              alt=""
              aria-hidden="true"
              style={{
                position:"absolute", right:-30, bottom:-30,
                width:190, height:190, opacity:0.05, pointerEvents:"none", objectFit:"contain",
              }}
            />

            {mode === "form" && !success && (
              <div style={{ display:"flex", gap:4, marginBottom:32, position:"relative" }}>
                {STEPS.map(s => (
                  <motion.div key={s.id}
                    animate={{ flex:step>=s.id?2:1, background:step>s.id?T.red:step===s.id?T.gold:T.border }}
                    transition={{ duration:0.45 }}
                    style={{ height:3, borderRadius:2 }}/>
                ))}
              </div>
            )}

            <AnimatePresence mode="wait">
              {mode === "list" && (
                <motion.div key="list" variants={pv} initial="enter" animate="center" exit="exit" transition={{ duration:0.38 }} style={{ position:"relative" }}>
                  {renderList()}
                </motion.div>
              )}

              {mode === "form" && !success && (
                <motion.div key={`step-${step}`} variants={pv} initial="enter" animate="center" exit="exit"
                  transition={{ duration:0.38, ease:[0.22,1,0.36,1] }} style={{ position:"relative" }}>
                  {stepContent[stepKey]?.()}

                  {error && (
                    <motion.div initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }}
                      style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:T.redSoft, border:"1px solid #F3B9BE", marginBottom:14, marginTop:4, borderRadius:10 }}>
                      <X size={12} color={T.redDark}/>
                      <span style={{ fontSize:11, color:T.redDark, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.04em" }}>{error}</span>
                    </motion.div>
                  )}

                  <div style={{ display:"grid", gridTemplateColumns:step>1?"1fr 2fr":"1fr", gap:10, marginTop:20 }}>
                    {step > 1 && (
                      <button onClick={goPrev}
                        style={{ padding:"14px 0", border:`1.5px solid ${T.border}`, background:T.bg, color:T.muted, cursor:"pointer", fontSize:10, fontWeight:700, letterSpacing:"0.18em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:6, borderRadius:12, boxShadow:"0 4px 0 #EEDFC4", transition:"all .2s" }}
                        onMouseEnter={e=>{ e.currentTarget.style.borderColor=T.black; e.currentTarget.style.color=T.black; }}
                        onMouseLeave={e=>{ e.currentTarget.style.borderColor=T.border; e.currentTarget.style.color=T.muted; }}>
                        <ArrowLeft size={12}/> BACK
                      </button>
                    )}
                    <button
                      onClick={goNext}
                      disabled={saving || uploading || (step === TERMS_STEP && !termsAccepted)}
                      style={{
                        padding:"14px 0", border:"none",
                        background:(saving||uploading||(step===TERMS_STEP&&!termsAccepted))?T.mutedSoft:T.black,
                        color:T.bg,
                        cursor:(saving||uploading)?"wait":(step===TERMS_STEP&&!termsAccepted)?"not-allowed":"pointer",
                        fontSize:10, fontWeight:700, letterSpacing:"0.2em", fontFamily:"'Montserrat',sans-serif",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8, borderRadius:12,
                        boxShadow:(saving||uploading||(step===TERMS_STEP&&!termsAccepted))?"none":"0 4px 0 #3B2013, 0 8px 20px rgba(27,14,6,.24)",
                        transition:"opacity .2s, background .2s",
                      }}>
                      {saving
                        ? <><span style={{ width:12,height:12,border:`2px solid ${T.bg}`,borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",animation:"glamSpin 1s linear infinite" }}/> SAVING…</>
                        : uploading
                          ? <><span style={{ width:12,height:12,border:`2px solid ${T.bg}`,borderTopColor:"transparent",borderRadius:"50%",display:"inline-block",animation:"glamSpin 1s linear infinite" }}/> UPLOADING…</>
                          : step === LAUNCH_STEP
                            ? <><CheckCircle2 size={13}/> {isEdit?"SAVE CHANGES":"LAUNCH STORE"}</>
                            : <>CONTINUE <ChevronRight size={12}/></>
                      }
                    </button>
                  </div>

                  <div style={{ textAlign:"center", marginTop:16, fontSize:9, color:T.mutedSoft, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.2em" }}>
                    {STEPS[step-1]?.label.toUpperCase()} — {step} of {STEPS.length}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {success && <SuccessOverlay/>}
          </div>

          {mode === "form" && !isMobile && (
            <div style={{ marginTop:20, display:"flex", justifyContent:"center", gap:0 }}>
              {STEPS.map((s,i) => {
                const done   = step > s.id;
                const active = step === s.id;
                return (
                  <div key={s.id} style={{ display:"flex", alignItems:"center" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                      <div style={{ width:22, height:22, borderRadius:"50%", border:`1.5px solid ${done||active?"rgba(255,255,255,.7)":"rgba(255,255,255,.2)"}`, background:done?GRAD_GOLD:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, fontFamily:"'Montserrat',sans-serif", color:done?"#fff":active?"rgba(255,255,255,.9)":"rgba(255,255,255,.3)", transition:"all .3s" }}>
                        {done?<Check size={10} color="#fff"/>:s.id}
                      </div>
                      <span style={{ fontSize:8, letterSpacing:"0.15em", color:active?"rgba(255,255,255,.8)":"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif", fontWeight:active?700:400 }}>
                        {s.label.toUpperCase()}
                      </span>
                    </div>
                    {i < STEPS.length-1 && (
                      <div style={{ width:40, height:1, background:done?"rgba(255,255,255,.5)":"rgba(255,255,255,.1)", margin:"0 6px", marginBottom:20, transition:"background .3s" }}/>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommerceOnboarding;